import React, { useEffect, useState } from 'react'
import { api } from '../../services/api'
import { useCatalogStore } from '../../stores/catalogStore'

// Phase 3: single-select "Initiative" facet rendered at the top of the
// FacetPanel sidebar (above the dimension facet groups). The catalog store
// already tracks filters.initiative_id and URL-serializes it — this is the
// missing UI to set it.
//
// Per-option count badges are out of scope for v1 (would require a separate
// aggregate endpoint); the facet just sets the filter.

interface Initiative {
  id: number
  slug: string
  name: string
  description: string | null
}

export default function InitiativeFacet() {
  const initiativeId = useCatalogStore((s) => s.filters.initiative_id)
  const setInitiativeFilter = useCatalogStore((s) => s.setInitiativeFilter)
  const fetchEntries = useCatalogStore((s) => s.fetchEntries)

  const [initiatives, setInitiatives] = useState<Initiative[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get('/api/v1/initiatives')
      .then((data: Initiative[]) => {
        if (!cancelled) setInitiatives(data || [])
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load initiatives'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setInitiativeFilter(value === '' ? null : value)
    fetchEntries()
  }

  // Normalize for the controlled <select>: store holds id as a string or
  // null; an empty string maps to the "All initiatives" sentinel.
  const selectValue = initiativeId == null ? '' : String(initiativeId)

  return (
    <div className="border-b border-slate-200 py-4">
      <label
        htmlFor="initiative-facet"
        className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"
      >
        Initiative
      </label>
      {error ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : (
        <select
          id="initiative-facet"
          value={selectValue}
          onChange={handleChange}
          disabled={loading}
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
        >
          <option value="">All initiatives</option>
          {initiatives.map((init) => (
            <option key={init.id} value={init.id}>
              {init.name}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}
